// RITA-1533 (V8) — Gewählte und wirksame Lage der Platzhalterleiste. Die Wahl kommt aus dem localStorage
// (je Rechner und Browser) und bleibt dort unverändert; die wirksame Lage folgt der Breite des Flex-Rahmens
// (`wirksameLeistenposition`): Hat das ganze Blatt neben der Leiste keinen Platz, steht sie oben (mit Hinweis)
// und kehrt zurück, sobald der Platz reicht. Gemessen wird vor dem ersten Zeichnen (useLayoutEffect) – ein
// schmal geöffnetes Fenster zeigt die Leiste sofort oben – und danach per ResizeObserver am Rahmen (Fenster,
// Menü auf/zu). Die Regel hängt am Rahmen, nicht an der Fensterbreite.
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  istLeistenposition,
  LEISTE_SPEICHERSCHLUESSEL,
  LEISTE_STANDARDPOSITION,
  Leistenposition,
  wirksameLeistenposition,
} from './leistenplatz';

/** Gemerkte Wahl (je Browser); unbekannte oder fehlende Werte → Standard. */
export function gemerkteLeistenposition(): Leistenposition {
  try {
    const wert = window.localStorage.getItem(LEISTE_SPEICHERSCHLUESSEL);
    return istLeistenposition(wert) ? wert : LEISTE_STANDARDPOSITION;
  } catch {
    return LEISTE_STANDARDPOSITION;
  }
}

export function leistenpositionMerken(position: Leistenposition): void {
  try {
    window.localStorage.setItem(LEISTE_SPEICHERSCHLUESSEL, position);
  } catch {
    // Speichern ist nur eine Bequemlichkeit (privates Fenster, gesperrter Speicher): stillschweigend übergehen.
  }
}

export default function useLeistenposition(bedarfPx: number) {
  const [gewaehlt, setGewaehlt] = useState<Leistenposition>(gemerkteLeistenposition);
  const [rahmenBreite, setRahmenBreite] = useState<number | null>(null);
  const rahmenRef = useRef<HTMLDivElement>(null);
  /** Stand die Leiste zuletzt wegen Platzmangels oben? Nur dann gilt beim Rückweg der Spielraum. */
  const erzwungenRef = useRef(false);

  useLayoutEffect(() => {
    const rahmen = rahmenRef.current;
    if (!rahmen) return undefined;
    let frame = 0;
    const messen = () => {
      frame = 0;
      // Beim Drucken sind die Leisten ausgeblendet und der Rahmen anders breit: letzte Bildschirmmessung behalten.
      if (window.matchMedia?.('print').matches) return;
      const breite = rahmen.getBoundingClientRect().width;
      setRahmenBreite((alt) => (alt === breite ? alt : breite));
    };
    // Messwerte ändern das Layout. Nicht innerhalb der ResizeObserver-Zustellung schreiben.
    const planen = () => {
      if (!frame) frame = window.requestAnimationFrame(messen);
    };
    messen();
    const beobachter = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(planen);
    beobachter?.observe(rahmen);
    window.addEventListener('resize', planen);
    window.addEventListener('afterprint', planen);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      beobachter?.disconnect();
      window.removeEventListener('resize', planen);
      window.removeEventListener('afterprint', planen);
    };
  }, []);

  const wirksam = wirksameLeistenposition(gewaehlt, rahmenBreite, bedarfPx, erzwungenRef.current);
  // Nach dem Zeichnen merken, nicht während des Renderns: der Wert zählt erst für die nächste Messung.
  useLayoutEffect(() => {
    erzwungenRef.current = wirksam === 'oben' && gewaehlt !== 'oben';
  });
  const waehlen = useCallback((position: Leistenposition) => {
    setGewaehlt(position);
    leistenpositionMerken(position);
  }, []);

  return { rahmenRef, gewaehlt, wirksam, rahmenBreite, waehlen };
}
